/**
 * ShareCourseSheet - Bottom sheet for sharing course link.
 * Allows copying deep link and sending to selected users via Telegram.
 */

import { useState, useCallback } from "react";
import { BottomSheet } from "../../../../components/ui/BottomSheet.tsx";
import {
  IconSearch,
  IconUser,
  IconCheck,
  IconLink,
  IconSend,
} from "../../../../components/Icons.tsx";
import { haptic, safeShowAlert } from "../../../../lib/telegram.ts";
import { config } from "../../../../config.ts";
import { useDebouncedValue } from "../../../../lib/use-debounce.ts";
import {
  useSearchAccounts,
  type SearchAccountResult,
} from "../../shared/hooks/useSearchAccounts";
import { useMutation } from "urql";

// =============================================================================
// MUTATION
// =============================================================================

const SendCourseInviteDocument = `
  mutation SendCourseInvite($courseId: ID!, $accountIds: [ID!]!) {
    sendCourseInvite(courseId: $courseId, accountIds: $accountIds) {
      success
      sentCount
      message
    }
  }
`;

interface SendCourseInviteResult {
  success: boolean;
  sentCount: number;
  message: string;
}

// =============================================================================
// HELPERS
// =============================================================================

function formatAccountName(account: SearchAccountResult): string {
  const parts = [account.firstName, account.lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  return account.username ? `@${account.username}` : "Пользователь";
}

// =============================================================================
// COMPONENT
// =============================================================================

interface ShareCourseSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courseId: string;
  courseTitle: string;
}

export function ShareCourseSheet({
  open,
  onOpenChange,
  courseId,
  courseTitle,
}: ShareCourseSheetProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAccounts, setSelectedAccounts] = useState<SearchAccountResult[]>([]);
  const [courseLinkCopied, setCourseLinkCopied] = useState(false);
  const [academyLinkCopied, setAcademyLinkCopied] = useState(false);

  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  const { accounts: searchResults, isLoading: isSearching } = useSearchAccounts({
    query: debouncedQuery,
    limit: 20,
    pause: !open,
  });

  const [sendResult, executeSendMutation] = useMutation<{
    sendCourseInvite: SendCourseInviteResult;
  }>(SendCourseInviteDocument);

  const courseDeepLink = config.academyCourseDeepLink(courseId);
  const academyDeepLink = config.academyDeepLink;

  const handleCopyCourseLink = useCallback(async () => {
    haptic("light");
    try {
      await navigator.clipboard.writeText(courseDeepLink);
      setCourseLinkCopied(true);
      haptic("success");
      setTimeout(() => setCourseLinkCopied(false), 2000);
    } catch {
      haptic("error");
      prompt("Скопируйте ссылку:", courseDeepLink);
    }
  }, [courseDeepLink]);

  const handleCopyAcademyLink = useCallback(async () => {
    haptic("light");
    try {
      await navigator.clipboard.writeText(academyDeepLink);
      setAcademyLinkCopied(true);
      haptic("success");
      setTimeout(() => setAcademyLinkCopied(false), 2000);
    } catch {
      haptic("error");
      prompt("Скопируйте ссылку:", academyDeepLink);
    }
  }, [academyDeepLink]);

  const handleSelectAccount = useCallback((account: SearchAccountResult) => {
    haptic("light");
    setSelectedAccounts((prev) => {
      const exists = prev.some((a) => a.id === account.id);
      if (exists) {
        return prev.filter((a) => a.id !== account.id);
      }
      return [...prev, account];
    });
  }, []);

  const handleRemoveAccount = useCallback((accountId: string) => {
    haptic("light");
    setSelectedAccounts((prev) => prev.filter((a) => a.id !== accountId));
  }, []);

  const handleSend = useCallback(async () => {
    if (selectedAccounts.length === 0) return;

    haptic("light");
    try {
      const result = await executeSendMutation({
        courseId,
        accountIds: selectedAccounts.map((a) => a.id),
      });

      if (result.data?.sendCourseInvite.success) {
        haptic("success");
        const msg =
          result.data.sendCourseInvite.message ||
          `Отправлено: ${result.data.sendCourseInvite.sentCount}`;
        safeShowAlert(msg);
        setSelectedAccounts([]);
        setSearchQuery("");
        onOpenChange(false);
      } else {
        haptic("error");
        safeShowAlert(
          result.data?.sendCourseInvite.message || "Не удалось отправить"
        );
      }
    } catch {
      haptic("error");
      safeShowAlert("Ошибка отправки");
    }
  }, [selectedAccounts, courseId, executeSendMutation, onOpenChange]);

  const isSending = sendResult.fetching;

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Поделиться"
      footer={
        selectedAccounts.length > 0 ? (
          <button
            type="button"
            onClick={handleSend}
            disabled={isSending}
            className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-blue-600 text-white font-medium disabled:opacity-50 active:scale-[0.99] transition-transform"
          >
            {isSending ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <IconSend className="w-4 h-4" />
                Отправить ({selectedAccounts.length})
              </>
            )}
          </button>
        ) : undefined
      }
    >
      <div className="space-y-3">
        {/* Copy course link */}
        <button
          type="button"
          onClick={handleCopyCourseLink}
          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all active:scale-[0.99] ${
            courseLinkCopied
              ? "bg-emerald-50 border-emerald-200"
              : "bg-slate-50 border-slate-200 active:bg-slate-100"
          }`}
        >
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
            courseLinkCopied ? "bg-emerald-500 text-white" : "bg-blue-500 text-white"
          }`}>
            {courseLinkCopied ? <IconCheck className="w-4 h-4" /> : <IconLink className="w-4 h-4" />}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className={`text-[14px] font-medium ${courseLinkCopied ? "text-emerald-700" : "text-slate-900"}`}>
              {courseLinkCopied ? "Скопировано!" : "Ссылка на курс"}
            </p>
            <p className="text-[12px] text-slate-400 truncate">{courseTitle}</p>
          </div>
        </button>

        {/* Copy academy link */}
        <button
          type="button"
          onClick={handleCopyAcademyLink}
          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all active:scale-[0.99] ${
            academyLinkCopied
              ? "bg-emerald-50 border-emerald-200"
              : "bg-slate-50 border-slate-200 active:bg-slate-100"
          }`}
        >
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
            academyLinkCopied ? "bg-emerald-500 text-white" : "bg-slate-400 text-white"
          }`}>
            {academyLinkCopied ? <IconCheck className="w-4 h-4" /> : <IconLink className="w-4 h-4" />}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className={`text-[14px] font-medium ${academyLinkCopied ? "text-emerald-700" : "text-slate-900"}`}>
              {academyLinkCopied ? "Скопировано!" : "Ссылка на академию"}
            </p>
            <p className="text-[12px] text-slate-400">Все курсы</p>
          </div>
        </button>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-[11px] text-slate-400 uppercase tracking-wide">
            или отправить
          </span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* Selected accounts chips */}
        {selectedAccounts.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {selectedAccounts.map((account) => (
              <button
                key={account.id}
                type="button"
                onClick={() => handleRemoveAccount(account.id)}
                className="flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-blue-100 text-blue-700 text-[12px] font-medium active:bg-blue-200 transition-colors"
              >
                <span className="truncate max-w-[80px]">
                  {account.firstName || account.username || "User"}
                </span>
                <span className="w-4 h-4 rounded-full bg-blue-200 text-blue-600 flex items-center justify-center text-[10px]">
                  ×
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Search input */}
        <div className="relative">
          <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск по имени..."
            className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-slate-200 bg-white text-[14px] placeholder:text-slate-400 focus:outline-none focus:border-blue-400 transition-colors"
            autoComplete="off"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-xs active:bg-slate-300"
            >
              ×
            </button>
          )}
        </div>

        {/* Search results */}
        <div className="space-y-1">
          {/* Empty state */}
          {!searchQuery && searchResults.length === 0 && (
            <p className="text-[13px] text-slate-400 text-center py-3">
              Введите имя для поиска
            </p>
          )}

          {/* Searching */}
          {searchQuery.length >= 2 && isSearching && (
            <div className="flex items-center justify-center gap-2 py-3 text-slate-400">
              <div className="w-4 h-4 border-2 border-slate-200 border-t-blue-500 rounded-full animate-spin" />
              <span className="text-[13px]">Поиск...</span>
            </div>
          )}

          {/* No results */}
          {searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && (
            <p className="text-[13px] text-slate-400 text-center py-3">
              Не найдено
            </p>
          )}

          {/* Results list */}
          {searchResults.length > 0 && !isSearching && (
            <div className="space-y-1">
              {searchResults.map((account) => {
                const isSelected = selectedAccounts.some((a) => a.id === account.id);
                return (
                  <button
                    key={account.id}
                    type="button"
                    onClick={() => handleSelectAccount(account)}
                    className={`w-full flex items-center gap-2.5 p-2.5 rounded-xl transition-all text-left ${
                      isSelected
                        ? "bg-blue-50 ring-2 ring-blue-400"
                        : "bg-slate-50 active:bg-slate-100"
                    }`}
                  >
                    <div
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                        isSelected ? "bg-blue-500 text-white" : "bg-slate-200 text-slate-400"
                      }`}
                    >
                      {isSelected ? <IconCheck className="w-4 h-4" /> : <IconUser className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate text-[13px] ${isSelected ? "text-blue-900" : "text-slate-900"}`}>
                        {formatAccountName(account)}
                      </p>
                      {account.username && (
                        <p className={`text-[11px] ${isSelected ? "text-blue-500" : "text-slate-400"}`}>
                          @{account.username}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}
